import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Ticket, Plus, Copy, Trash2, Power, X, Calendar as CalendarIcon, Hash, Tag, Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const DEFAULT_CODES = new Set(["ICON2026", "BETATESTER", "ICFAMILY", "EARLYBIRD"]);

const AdminPromoCodes = () => {
  const { getAuthHeaders } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // create form state
  const [form, setForm] = useState({
    code: "",
    label: "",
    duration_days: 30,
    max_redemptions: "",
    expires_at: "",
  });
  const [creating, setCreating] = useState(false);

  const headers = useCallback(
    () => ({ ...getAuthHeaders(), "Content-Type": "application/json" }),
    [getAuthHeaders]
  );

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promos`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setCodes(res.data.codes || []);
    } catch (err) {
      if (err.response?.status === 403) {
        // silently ignore — non-admins won't see this widget
        setCodes([]);
      } else {
        toast.error("Failed to load promo codes");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const resetForm = () =>
    setForm({ code: "", label: "", duration_days: 30, max_redemptions: "", expires_at: "" });

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim().toUpperCase(),
      label: form.label.trim(),
      duration_days: Number(form.duration_days),
    };
    if (form.max_redemptions !== "" && Number(form.max_redemptions) > 0) {
      payload.max_redemptions = Number(form.max_redemptions);
    }
    if (form.expires_at) {
      // ISO datetime at end of day UTC
      payload.expires_at = new Date(`${form.expires_at}T23:59:59Z`).toISOString();
    }
    if (!payload.code || !payload.label || !payload.duration_days) {
      toast.error("Code, label, and duration are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/promos`, payload, {
        headers: headers(),
        withCredentials: true,
      });
      toast.success(`Promo code ${payload.code} created`);
      setCreateOpen(false);
      resetForm();
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create promo code");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (code, nextActive) => {
    try {
      await axios.patch(
        `${API}/admin/promos/${encodeURIComponent(code.code)}`,
        { active: nextActive },
        { headers: headers(), withCredentials: true }
      );
      setCodes((prev) =>
        prev.map((c) => (c.code === code.code ? { ...c, active: nextActive } : c))
      );
      toast.success(`${code.code} ${nextActive ? "activated" : "deactivated"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update code");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/promos/${encodeURIComponent(deleteTarget.code)}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success(`${deleteTarget.code} deleted`);
      setCodes((prev) => prev.filter((c) => c.code !== deleteTarget.code));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete code");
    } finally {
      setDeleteTarget(null);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code} to clipboard`);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="admin-promo-codes-section"
    >
      <div className="p-6 border-b border-slate-200 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Ticket className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Promo Codes</h2>
            <p className="text-sm text-slate-500">
              Mint codes that grant extended free access at sign-up or in-app.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-primary text-white hover:bg-primary/90 rounded-full"
          data-testid="admin-create-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New code
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="p-10 text-center">
          <Ticket className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">No promo codes yet.</p>
          <p className="text-sm text-slate-500">Create one to grant extended free access.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-promo-table">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Code</th>
                <th className="text-left px-6 py-3 font-medium">Label</th>
                <th className="text-left px-6 py-3 font-medium">Duration</th>
                <th className="text-left px-6 py-3 font-medium">Redeemed</th>
                <th className="text-left px-6 py-3 font-medium">Expires</th>
                <th className="text-left px-6 py-3 font-medium">Active</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {codes.map((c) => {
                const isSeed = DEFAULT_CODES.has(c.code);
                const limitText = c.max_redemptions ? ` / ${c.max_redemptions}` : "";
                return (
                  <tr
                    key={c.code}
                    className="hover:bg-slate-50"
                    data-testid={`promo-row-${c.code}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-900">{c.code}</span>
                        <button
                          onClick={() => copyToClipboard(c.code)}
                          className="text-slate-400 hover:text-primary"
                          title="Copy code"
                          data-testid={`copy-${c.code}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {isSeed && (
                          <Badge className="bg-slate-100 text-slate-600 text-[10px] py-0">
                            seed
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{c.label}</td>
                    <td className="px-6 py-4 text-slate-700">{c.duration_days} days</td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.redemption_count || 0}
                      {limitText}
                    </td>
                    <td className="px-6 py-4 text-slate-500">{formatDate(c.expires_at)}</td>
                    <td className="px-6 py-4">
                      <Switch
                        checked={!!c.active}
                        onCheckedChange={(v) => handleToggleActive(c, v)}
                        data-testid={`toggle-${c.code}`}
                      />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        disabled={isSeed}
                        onClick={() => setDeleteTarget(c)}
                        data-testid={`delete-${c.code}`}
                        title={isSeed ? "Default seed codes can't be deleted" : "Delete code"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-md" data-testid="create-promo-dialog">
          <DialogHeader>
            <DialogTitle>Create promo code</DialogTitle>
            <DialogDescription>
              Mint a new code for a beta tester, partner, or campaign.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="new-code" className="text-slate-700 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" /> Code
              </Label>
              <Input
                id="new-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. RSVP2026"
                className="uppercase tracking-wide font-mono"
                required
                maxLength={32}
                data-testid="new-code-input"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="new-label" className="text-slate-700 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Label
              </Label>
              <Input
                id="new-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Conference RSVP — 3 months free"
                required
                maxLength={120}
                data-testid="new-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="new-days" className="text-slate-700 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Duration (days)
                </Label>
                <Input
                  id="new-days"
                  type="number"
                  min={1}
                  max={3650}
                  value={form.duration_days}
                  onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                  required
                  data-testid="new-days-input"
                />
              </div>
              <div>
                <Label htmlFor="new-max" className="text-slate-700">Max redemptions</Label>
                <Input
                  id="new-max"
                  type="number"
                  min={1}
                  value={form.max_redemptions}
                  onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                  placeholder="Unlimited"
                  data-testid="new-max-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new-expires" className="text-slate-700 flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5" /> Expires on (optional)
              </Label>
              <Input
                id="new-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="new-expires-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setCreateOpen(false); resetForm(); }}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary text-white hover:bg-primary/90"
                disabled={creating}
                data-testid="submit-new-promo-btn"
              >
                {creating ? "Creating…" : "Create code"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promo code?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  Permanently delete <span className="font-mono font-semibold">{deleteTarget.code}</span>?
                  {deleteTarget.redemption_count > 0 && (
                    <>
                      {" "}This code has been redeemed{" "}
                      <strong>{deleteTarget.redemption_count}</strong> time
                      {deleteTarget.redemption_count > 1 ? "s" : ""} — those users
                      keep their access. Consider deactivating instead.
                    </>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-delete-promo-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPromoCodes;
